import styled, {css, keyframes, ThemeContext} from "styled-components";
import React, {ReactNode, useContext, useEffect, useRef, useState} from "react";
import AnswerSearch from "./AnswerSearch";
import {Answer, Guess, GuessResult, ImageHint, Stats, TileIndex} from "../types/types";
import {findAnswer} from "../data";
import Countdown from "./Countdown";
import {AppContext} from "../context/appContext";
import loadStats from '../helpers/stats'
import DistributionGraph from "./DistributionGraph";

const statsDB = loadStats()
const defaultStats = statsDB.getStats()

const windowWidth = window.innerWidth
const windowHeight = window.innerHeight

let currentMeta : ImageHint
function getCurrentImage (images: ImageHint[]) : ImageHint {
	if (currentMeta) {
		return currentMeta
	}
	let now = new Date()

	const parms = new URLSearchParams(window.location.search)
	let daysToSkip = parseInt(parms.get('skip') || '0')


	if (!isNaN(daysToSkip)) {
		now = new Date(now.getTime() + (daysToSkip * 60 * 60 * 1000 * 24))
	}

	for (let i = images.length - 1; i >= 0; i --) {
		const s = images[i]
		const start = new Date(s.startDate)
		if (start < now) {
			return s
		}
	}
	currentMeta = {
		id: '?',
		number: 1,
		answerKey: '?',
		imageUrl: '?',
		startDate: new Date().toISOString(),
		tileRevealOrder: [0,1,2,3,4,5,6,7,8]
	}
	return currentMeta
}

function getNextImage (images: ImageHint[]) : ImageHint | null {
	const now = new Date()
	return images.find(s => {
		const start = new Date(s.startDate)
		return start > now
	}) || null
}

type ActiveModal = null | 'help' | 'stats'

export default function Game () {
	const theme = useContext(ThemeContext)

	const [imageLoaded, setImageLoaded] = useState(false)
	const [imageHeight, setImageHeight] = useState(0)
	const [imageWidth, setImageWidth] = useState(0)
	const [guesses, setGuesses] = useState<Guess[]>([])
	const [selectedAnswer, setSelectedAnswer] = useState<Answer | null>(null)
	const [gameIsOver, setGameIsOver] = useState(false)
	const [shareButtonText, setShareButtonText] = useState('Share Your Result')
	const [activeModal, setActiveModal] = useState<ActiveModal>(null)
	const [stats, setStats] = useState<Stats>(defaultStats)
	const [animateTiles, setAnimateTiles] = useState(false)

	const guessesContainerRef = useRef<HTMLDivElement>(null)
	const actionsContainerRef = useRef<HTMLDivElement>(null)
	const headerContainerRef = useRef<HTMLDivElement>(null)

	const app = useContext(AppContext)
	const {images} = app
	const image = getCurrentImage(images)
	const nextImage = getNextImage(images)
	const isModalOpen = activeModal !== null

	if (!image) {
		throw new Error(`No ss found`)
	}

	const guessedKeys : string[] = guesses.reduce((keys : string[], guess) => {
		if (guess.answer) {
			keys.push(guess.answer.key)
		}

		return keys
	}, [])

	const lastGuess = guesses[guesses.length-1]
	let hasWon = false
	if (lastGuess) {
		hasWon = lastGuess.result === 'correct'
	}

	const correctAnswer = findAnswer(app.answers, image.answerKey)

	let visibleTiles : TileIndex[] = []
	if (hasWon) {
		visibleTiles = [...image.tileRevealOrder]
	} else {
		for (let i = 0; i <= guesses.length; i++) {
			visibleTiles.push(image.tileRevealOrder[i])
		}
	}

	useEffect(() => {
		let helped = localStorage.getItem('helpModal')
		if (!helped) {
			setActiveModal('help')
		}
	}, [])

	useEffect(() => {
		if (window.location.hostname !== app.domain && app.domain) {
			const redirectTo = 'https://' + app.domain
			console.log('redirect to ', redirectTo)
			if (window.location.hostname.indexOf('localhost') === -1) {
				window.location.href = redirectTo
			}
		}
	}, [])

	useEffect(() => {
		if (window.location.search.indexOf('win') >= 0) {
			addGuess({
				answer: correctAnswer,
				result: 'correct',
			})
		}
		if (window.location.search.indexOf('lose') >= 0) {
			setGameIsOver(true)
		}
	}, [])

	useEffect(() => {
		const g = guesses[guesses.length - 1]
		if (g && g.result === 'correct') {
			setGameIsOver(true)
			setSelectedAnswer(null)
		} else if (guesses.length === image.tileRevealOrder.length) {
			setGameIsOver(true)
		}
	}, [guesses.length])

	useEffect(() => {
		if (gameIsOver) {
			let statsDelay : number
			if (hasWon) {
				setAnimateTiles(true)
				statsDelay = 2500
			} else {
				statsDelay = 2000
			}
			setTimeout(() => {
				setActiveModal('stats')
			}, statsDelay)
		}
	}, [gameIsOver])

	useEffect(() => {
		const currentGame = statsDB.getGameResult(image.id)
		if (!currentGame) {
			return
		}
		const newGuesses = currentGame.guesses
		/*
		.map((g) : Guess => {
			if (!g) {
				return{
					result: 'skip'
				}
			} else {
				let answer = findAnswer(app.answers, g)
				if (g === image.answerKey) {
					won = true
					return {
						answer: {
							...answer
						},
						result: 'correct'
					}
				} else {
					return {
						answer: {
							...answer
						},
						result: 'wrong'
					}
				}
			}
		})
		 */
		setGuesses([...newGuesses])
	}, [])

	useEffect(() => {
		if (!image.imageUrl) {
			return
		}
		const img = new Image()
		img.src = image.imageUrl
		img.addEventListener('load', () => {
			setImageLoaded(true)
			let width = img.naturalWidth
			let height = img.naturalHeight
			const widthByHeight = width / height

			const guessesHeight = Math.floor(guessesContainerRef?.current?.getBoundingClientRect().height || 0)
			const actionsHeight = Math.floor(actionsContainerRef?.current?.getBoundingClientRect().height || 0)
			const headerHeight = Math.floor(headerContainerRef?.current?.getBoundingClientRect().height || 0)
			const maxHeight = windowHeight - guessesHeight - actionsHeight - headerHeight - 1
			const tilesPadding = theme.tilesSidePadding * 2
			const maxWidth = Math.min(theme.maxWidthPX - tilesPadding, windowWidth - tilesPadding, img.naturalWidth)

			if (width > maxWidth) {
				width = maxWidth
				height = width / widthByHeight
			}

			if (height > maxHeight) {
				height = maxHeight
				width = height * widthByHeight
			}

			setImageWidth(width)
			setImageHeight(height)
		})
	}, [image.imageUrl])

	function onSubmit () {
		if (guesses.length === image.tileRevealOrder.length) {
			return
		}
		if (!selectedAnswer) {
			return
		}
		if (image.answerKey === selectedAnswer.key) {
			addGuess({
				result: 'correct',
				answer: {
					...selectedAnswer
				}
			})
			return
		}
		addGuess({
			answer: {
				...selectedAnswer
			},
			result: 'wrong',
		})
		setSelectedAnswer(null)
	}

	function addGuess (g: Guess) {
		guesses.push(g)
		setGuesses([...guesses])
		saveGameGuesses()
	}

	function clickSkip () {
		if (guesses.length === image.tileRevealOrder.length) {
			return
		}
		addGuess({
			result: 'skip',
		})
		setSelectedAnswer(null)
	}

	function saveGameGuesses () {
		statsDB.saveGameGuesses(image.id, guesses)
		setStats(statsDB.getStats())
	}

	function closeModal () {
		if (activeModal === 'help') {
			localStorage.setItem('helpModal', 'true')
		}
		setActiveModal(null)
	}

	function shareResults () {
		setShareButtonText('Copied!')
		let result = app.resultCopyHeader(image.number || 0) + `
`
		let squares : string[] = ['⬜', '⬜', '⬜', '⬜', '⬜', '⬜', '⬜', '⬜', '⬜'] // black squares
		guesses.forEach((guess, i) => {
			let tileIdx = image.tileRevealOrder[i]
			let icon : string
			if (guess.result === 'skip') {
				icon = '🟨' // yellow square
			} else if (guess.result === 'wrong') {
				icon = '🟥' // red square
			} else {
				icon = '🟩' // green square
			}

			squares[tileIdx] = icon
		})

		squares.forEach((square, i) => {
			result += square

			// After every third square, do a new line
			if ((i+1) % 3 === 0) {
				result += '\n'
			}
		})

		result += app.resultCopyFooter
		navigator.clipboard.writeText(result)
		console.log(result)
		setTimeout(() => {
			setShareButtonText('Share Your Results')
		}, 1500)
	}

	let tiles : ReactNode[] = []
	let tileIdx : TileIndex = 0

	const revealBlurs = app.blurSteps || [3, 1.5, 0.75, 0.5]
	const blur = revealBlurs[visibleTiles.length-1] || 0

	for (let row = 1; row <= 3; row++) {
		for (let col = 1; col <= 3; col++) {
			let idx = tileIdx
			let visible = visibleTiles.findIndex(x => {
				return x === tileIdx
			}) >= 0

			const tile = <ImageTile
				bgHeight={imageHeight}
				visible={visible}
				blur={blur}
				data-visible={visible}
				row={row}
				col={col}
				key={idx}
				imageUrl={image.imageUrl}
				animate={animateTiles}
				delay={idx*60}
				bgWidth={imageWidth}>
				<div className="flip-card-inner">
					<div className="front">
					</div>
					<div className="back">
					</div>
				</div>
			</ImageTile>
			tiles.push(tile)
			tileIdx++
		}
	}

	const guessC : ReactNode[] = []
	const guessIconSize = 20
	for (let i = 0; i < 9; i++) {
		const guess = guesses[i]
		if (!guess) {
			const c = <GuessRow key={i} result={'skip'} active={i === guesses.length} />
			guessC.push(c)
			continue
		}
		let text = guess.result === 'skip' ? 'Skipped' : guess.answer?.name
		let icon;
		if (guess.result === 'skip') {
			icon = <svg xmlns="http://www.w3.org/2000/svg" className="text-custom-mg" width={guessIconSize} height={guessIconSize} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
				<rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
			</svg>
		} else if (guess.result === 'wrong') {
			icon = <svg className="text-custom-negative" xmlns="http://www.w3.org/2000/svg" width={guessIconSize} height={guessIconSize}
				viewBox="0 0 24 24" fill="none" stroke={theme.colorWrong} strokeWidth="2" strokeLinecap="round"
				strokeLinejoin="round">
				<line x1="18" y1="6" x2="6" y2="18"></line>
				<line x1="6" y1="6" x2="18" y2="18"></line>
			</svg>
		} else if (guess.result === 'correct') {
			icon = <svg xmlns="http://www.w3.org/2000/svg"  x="0px" y="0px" viewBox="0 0 17.837 17.837" width={16} height={16} style={{marginRight: '4px'}}>
				<g>
					<path fill={theme.colorRight} d="M16.145,2.571c-0.272-0.273-0.718-0.273-0.99,0L6.92,10.804l-4.241-4.27   c-0.272-0.274-0.715-0.274-0.989,0L0.204,8.019c-0.272,0.271-0.272,0.717,0,0.99l6.217,6.258c0.272,0.271,0.715,0.271,0.99,0   L17.63,5.047c0.276-0.273,0.276-0.72,0-0.994L16.145,2.571z"/>
				</g>
			</svg>
		}

		const c = <GuessRow key={i} result={guess.result} active={i === guesses.length}>
			{icon}
			<div>{text}</div>
		</GuessRow>
		guessC.push(c)
	}


	let resultTitle : ReactNode

	if (hasWon) {
		resultTitle = app.resultTitleWin(guesses.length)
	} else {
		resultTitle = app.resultTitleLose()
	}

	function clickModalContainer (e: any) {
		const target = e.target
		if (target.getAttribute('data-name') === 'modal-container') {
			closeModal()
		}
	}

	return <GameContainer>
		<ModalContainer data-name={'modal-container'} visible={isModalOpen} onClick={clickModalContainer}>
			{activeModal === 'help' && <Modal visible={true}>
				<ModalHeader title={'How to Play'} onClose={closeModal} />
				<ModalBody>
					<ol>
						<li>See just a part of the image</li>
						<li>Wrong or skipped answers reveal more of the image</li>
						<li>Answer quickly and share your results!</li>
					</ol>
					<div style={{textAlign: 'center'}}>
						<PlayButton onClick={() => closeModal()}>Play</PlayButton>
					</div>
				</ModalBody>
			</Modal>}
			<Modal visible={activeModal === 'stats'}>
				<ModalHeader title={'Statistics'} onClose={closeModal} />
				<ModalBody>
					<StatsContainer>
						<Stat>
							<label>Times<br />Played</label>
							<div>{stats.played}</div>
						</Stat>
						<Stat>
							<label>Total<br />Wins</label>
							<div>{stats.wins}</div>
						</Stat>
						<Stat>
							<label>Current<br />Streak</label>
							<div>{stats.currentStreak}</div>
						</Stat>
						<Stat>
							<label>Longest<br />Streak</label>
							<div>{stats.longestStreak}</div>
						</Stat>
					</StatsContainer>
					<DistributionGraph distribution={stats.distributions} activeGuesses={gameIsOver ? guesses.length : undefined} />
					{gameIsOver && <StatsFooter>
						{nextImage && <>
							<CountdownContainer>
								<div className={"title"}>{app.countdownLabel}</div>
								<Countdown to={nextImage.startDate} onEnd={() => {window.location.reload()}} />
							</CountdownContainer>
						</>}
						<div className={'share-container'}>
							<ResultShare onClick={() => shareResults()}>
								{shareButtonText}
							</ResultShare>
						</div>
					</StatsFooter>}
				</ModalBody>
			</Modal>
		</ModalContainer>
		<GameHeader ref={headerContainerRef}>
			<div className={'title'}>{app.title}</div>
			<div className={'icons'}>
				<a href={app.donateLink}>
					<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"></path></svg>
				</a>
				<button onClick={() => {setActiveModal('stats')}}>
					<svg xmlns="http://www.w3.org/2000/svg" height="24" viewBox="0 0 24 24" width="24">
						<path fill={"currentColor"} stroke={"none"} d="M16,11V3H8v6H2v12h20V11H16z M10,5h4v14h-4V5z M4,11h4v8H4V11z M20,19h-4v-6h4V19z"></path>
					</svg>
				</button>
				<button onClick={() => {setActiveModal('help')}}>
					<svg xmlns="http://www.w3.org/2000/svg"
							 width="24" height="24"
							 viewBox="0 0 24 24" fill="none" stroke="currentColor"
							 strokeWidth="2"
							 strokeLinecap="round"
							 strokeLinejoin="round">
						<circle cx="12" cy="12" r="10"></circle><path d="M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3"></path><line x1="12" y1="17" x2="12.01" y2="17"></line>
					</svg>
				</button>
			</div>
		</GameHeader>
		<TilesContainer>
			{imageLoaded && (
				<ImageTiles width={imageWidth} height={imageHeight}>
					{tiles}
					{gameIsOver && <AnswerReveal hasCredit={!!image.creditText} delayMS={hasWon ? 1000 : 0}>
						<div className={'title'}>{correctAnswer.name}</div>
						<div className={'meta'}>{correctAnswer.meta}</div>
					</AnswerReveal>}
				</ImageTiles>
			)}

		</TilesContainer>
		<GuessContainer ref={guessesContainerRef}>
			{guessC}
		</GuessContainer>
		<ActionsContainer ref={actionsContainerRef}>
			<AnswerSearch
				onChange={(a) => {
					if (gameIsOver) {
						return
					}
					setSelectedAnswer(a)
				}}
				disabled={gameIsOver}
				onSubmit={onSubmit}
				value={selectedAnswer}
				filteredKeys={guessedKeys}
			/>
			<ButtonsContainer>
				<SkipButton disabled={gameIsOver} type={"button"} onClick={clickSkip}>Skip (Reveal a Tile)</SkipButton>
				<SubmitButton disabled={!selectedAnswer || gameIsOver} type={"button"} onClick={onSubmit}>Submit</SubmitButton>
			</ButtonsContainer>
		</ActionsContainer>
	</GameContainer>
}

type ModalHeaderProps = {
	title: string
	onClose: Function
}

function ModalHeader ({title, onClose} : ModalHeaderProps) {
	return <ModalHeaderContainer>
		<div className={'title'}>{title}</div>
		<a onClick={() => onClose()}>X</a>
	</ModalHeaderContainer>
}

const ModalContainer = styled.div<{visible: boolean}>`
	width: 100%;
	height: 100%;
	top: 0;
	left: 0;
	z-index: 6;
	position: fixed;
	align-items: center;
	justify-content: center;
	background: rgba(0,0,0,0.7);
	display: ${props => props.visible ? 'flex' : 'none'};
`

const modalIn = keyframes`
0% {
	transform: translateY(-50px);
	opacity: 0;
}

100% {
	transform: translateY(0px);
	opacity: 1;
}
`

const Modal = styled.div<{visible: boolean}>`
	display: ${props => props.visible ? 'block' : 'none'}; 
	padding: 1em 1em 1.5em 1em;
	border: 1px solid ${props => props.theme.colorDarkish};
	background: ${props => props.theme.colorDark};
	width: 98%;
	max-width: 468px;
	animation: ${modalIn} 0.3s ease;
	animation-fill-mode: forwards;
	transform: translateY(0px);
`

const ModalBody = styled.div`
 ol {
	list-style-type: numeric;
	list-style-position: inside;
	line-height: 1.4;
 }
 li {
 	margin-bottom: 1em;
 }
`

const ModalHeaderContainer = styled.div`
	display: grid;
	grid-template-columns: 1fr min-content;
	margin-bottom: 1em;
	color: ${props => props.theme.colorLighterGray};
	text-align: center;
`

const StatsContainer = styled.div`
	display: grid;
	grid-template-columns: repeat(4, 25%);
	margin-bottom: 2em;
	margin-top: 2em;
`

const Stat = styled.div`
	display: flex;
	flex-direction: column;
	text-align: center;	

	label {
		font-size: 12px;
		color: ${props => props.theme.colorLighterGray};
		margin-bottom: 0.5em;
	}
	div {
		font-size: 24px;
	}
`

const GameContainer = styled.div`
	display: grid;
	margin: 0 auto;
	grid-template-rows: min-content 1fr min-content min-content;
	height: 100%;
	width: 100%;
	background: ${props => props.theme.colorDark};
	max-width: ${props => props.theme.maxWidthPX}px;
`

const GameHeader = styled.div`
	padding: 0.5rem 0.25rem;
	display: flex;
	justify-content: space-between;
	border-bottom: 1px solid ${props => props.theme.colorLighterGray};
	
	.title {
		font-size: 24px;
		font-family: 'Roboto';
		color: ${props => props.theme.colorPrimary};
	}
	
	.icons {
		a, button {
			color: white;
			background: none;
			border: none;
			outline: none;
			cursor: pointer;
			padding: 0;
			margin: 0 0 0 3px;
		}
	}
`

const ImageTiles = styled.div<{width: number, height: number}>`
	display: grid;
	width: ${props => props.width}px;
	height: ${props => props.height}px;
	grid-template-rows: 1fr 1fr 1fr;
	grid-template-columns: 1fr 1fr 1fr;
	border: 1px solid black;
	margin: 0 auto;
	background: #333;
	grid-gap: 1px;
	position: relative;
	z-index: 1;
`

const GuessContainer = styled.div`
	flex-wrap: wrap;
	justify-content: space-between;
	align-content: flex-start;
	display: grid;
	padding: ${props => props.theme.guessGapPX}px;
	grid-template-columns: repeat(2, calc(50% - ${props => props.theme.guessGapPX/2}px));
	grid-gap: ${props => props.theme.guessGapPX}px;
	z-index: 2;
	grid-template-rows: repeat(5, ${props => props.theme.guessHeightPX}px);
`

const TilesContainer = styled.div`
	display: flex;
	position: relative;
	align-items: center;
  padding: 0 ${props => props.theme.tilesSidePadding}px;
`

const ActionsContainer = styled.div`
	padding: 6px 4px;
	z-index: 3;
`

const StatsFooter = styled.div`
	display: grid;
	grid-template-columns: 1fr 1fr;
	text-align: center;
	margin-top: 1em;
	padding-top: 1em;
	border-top: 1px solid ${props => props.theme.colorDarkish};
	
	.share-container {
		display: flex;
		align-items: flex-end;
		justify-content: center;
		width: 100%;
	}
`

const tileAnimation = keyframes`
0% {
	transform: scale(1);
}
50% {
	transform: scale(1.05);
}
100% {
	transform: scale(1);
}
`

const ImageTile = styled.div<{
	imageUrl: string,
	row: number,
	col: number,
	bgWidth: number,
	bgHeight: number,
	blur: number,
	visible: boolean,
	animate: boolean,
	delay: number,
}>`
	background: black;
	position: relative;

	div {
		position: absolute;
		width: 100%;
		height: 100%;
		left: 0px;
		transition: filter 0.5s ease, opacity 0.5s ease;
		top: 0px;
		filter: blur(${props => props.blur}px);
		opacity: ${props => props.visible ? 1 : 0};
		animation: ${props => props.animate ? css`${tileAnimation} 0.25s ease ${props.delay}ms` : ''};
		animation-fill-mode: forwards;
		background: url(${props => props.imageUrl}) no-repeat;
		background-size: ${props => props.bgWidth}px ${props => props.bgHeight}px;
		background-position: -${props => (props.col - 1) * props.bgWidth/3}px -${props => (props.row - 1) * props.bgHeight/3}px;
	}
`

const ButtonsContainer = styled.div`
	display: flex;
	justify-content: space-between;
	padding: 0.75em 0 0;
`

const Button = styled.button`
	padding: 0.75em 1.5em;
	color: white;
	border-radius: 5px;
	font-size: 14px;
	border: none;
	outline: none;
	cursor: pointer;
`

const SubmitButton = styled(Button)`
	background: ${props => props.theme.colorPrimary};
	color: ${props => props.theme.colorPrimaryOpposite}
`

const SkipButton = styled(Button)`
  background: ${props => props.theme.colorSkipBackground};
	color: ${props => props.theme.colorSkipText};
`

const GuessRow = styled.div<{result: GuessResult, active: boolean}>`
	border: 1px solid ${props => props.active ? props.theme.colorLighterGray : props.theme.guessColors[props.result].border};
	height: ${props => props.theme.guessHeightPX}px;
  background: ${props => props.theme.guessColors[props.result].bg || props.theme.colorDark};
	color: ${props => props.theme.guessColors[props.result].text};
	display: flex;
	align-items: center;
	padding-left: 3px;
	overflow: hidden;
	font-size: 12px;

	div {
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
		width: 100%;
		padding: 0 0.25em;
		line-height: ${props => props.theme.guessHeightPX}px;
		height: 100%;
	}
`

const answerReveal = keyframes`
	0% {
		opacity: 0;
		transform: translateY(-50px) scale(1.2);
	}

	100% {
		opacity: 1;
		transform: translateY(0px) scale(1);
	}
`

const AnswerReveal = styled.div<{hasCredit: boolean, delayMS: number}>`
	width: 98%;
	position: absolute;
	bottom: 1%;
	opacity: 0;
	left: 1%;
	color: ${props => props.theme.colorText};
	background: rgba(0, 0, 0, 0.75);
	padding: 0.5em 1em;
	text-align: center;
	animation: ${answerReveal} ease 0.5s;
	animation-delay: ${props => props.delayMS}ms;
	animation-fill-mode: forwards;

	.title {
		font-weight: bold;
		margin-bottom: 0.25em;
		font-size: 16px;
	}

	.meta {
		font-style: italic;
		font-size: 0.9em;
		font-size: 12px;
		color: ${props => props.theme.colorLightGray};
	}
`

const CountdownContainer = styled.div`
	div.title {
		color: ${props => props.theme.colorLighterGray};
		margin: 0 0 0.25em 0;
		font-size: 0.875rem;
    line-height: 1.25rem;
	}
`

const PlayButton = styled(Button)`
	background: ${props => props.theme.colorPrimary};
	color: ${props => props.theme.colorPrimaryOpposite};
`

const ResultShare = styled(Button)`
	background: ${props => props.theme.colorPrimary};
	color: ${props => props.theme.colorPrimaryOpposite};
	text-align: center;
	width: 12em;
`
